import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { useScrollTrack } from '@/lib/useScrollTrack';
import { RightRailWidgets } from '@/pages/timeline/components/RightRailWidgets';

interface RightRailProps {
  /** Page-supplied widgets. When omitted, the global timeline widgets render. */
  children?: ReactNode;
}

/**
 * Right dashboard rail (lg+). Pages may pass their own widgets via `children`;
 * otherwise the global trending-hashtags + suggested-people widgets render
 * (each manages its own loading / empty state).
 *
 * Scroll behaviour (2026-06-05): bidirectional scroll-following via
 * `useScrollTrack`. Page scrolls DOWN → rail moves up with the page; when
 * the rail's bottom meets the viewport bottom it stays there. Page reverses
 * to UP → rail IMMEDIATELY starts moving back down (revealing the top
 * cards) instead of waiting until the natural top scrolls into view. Page
 * keeps scrolling UP → rail's top eventually anchors under the Topbar.
 */
export function RightRail({ children }: RightRailProps) {
  const { slotRef, innerRef, tracking, style } = useScrollTrack();
  return (
    <aside
      ref={slotRef}
      className="hidden w-[250px] shrink-0 self-start lg:block"
    >
      <div
        ref={innerRef}
        className={cn('py-6 pl-2', tracking && 'fixed z-30')}
        style={style}
      >
        {children ?? <RightRailWidgets />}
      </div>
    </aside>
  );
}
